import { DATA_SOURCES } from '@/config/constants';
import {
    formatAddedOn,
    pluralDimensionToSingle,
    tikTokHealthStatKeys,
} from './sourceDataShaping';
import { getTikTokAndSearchHealthStats } from 'state/utils';
import createSourceDataHook from './createSourceDataHook';
import { getTikTokData } from '../api/tiktok.api';
import { indexEntities } from 'utils/entities';
import { shapeFlavors } from 'utils/helpers';

// Category and hashtag rows have no price.
const brandHealthStatKeys = [...tikTokHealthStatKeys, 'price point'];

const shapeRow =
    ({ type, entities, paginateBy }) =>
    (item) => {
        const { [type]: itemId } = item;
        const maybeItem = entities[itemId];
        let resolvedName;
        if (paginateBy === 'hashtags') {
            resolvedName = item?.hashtag_name;
        } else if (paginateBy === 'brands') {
            resolvedName = item.brand_name || maybeItem?.name;
        } else {
            resolvedName = maybeItem?.name;
        }
        const isBrandRow = paginateBy === 'brands';
        const healthStats = getTikTokAndSearchHealthStats({
            healthStatKeys: isBrandRow
                ? brandHealthStatKeys
                : tikTokHealthStatKeys,
            tikTokDataPoint: item,
            ...(isBrandRow && { price: maybeItem?.price ?? null }),
        });
        return {
            ...item,
            // The sortable/exportable form, as on the Google row.
            ...(isBrandRow && {
                price_segmentation: healthStats?.['price point']?.score,
            }),
            hierarchies: maybeItem?.hierarchies,
            name: resolvedName,
            depth: maybeItem?.depth,
            healthStats,
            flavors: shapeFlavors(maybeItem?.hierarchies),
            cluster: item.cluster ? `tiktok ${item.cluster}` : item.cluster,
            addedOn: formatAddedOn(item.added_on),
            ...((paginateBy !== 'hashtags' || item.brand_name) && {
                searchField: [
                    resolvedName,
                    item.hashtag_list || item.brand_name,
                ]
                    .filter(Boolean)
                    .join(' '),
            }),
            image: maybeItem?.image,
            type,
            fetching: false,
            fetchingData: false,
        };
    };

export const queryFnInfiniteScroll = ({ queryKey, pageParam }) =>
    queryFn({ queryKey: [queryKey[0], { ...queryKey[1], page: pageParam }] });

const queryFn = async ({ queryKey }) => {
    const resp = await getTikTokData({
        data: {
            ...queryKey[1],
            include_entities: true,
            week: queryKey[1].week
                ? queryKey[1].week === '*'
                    ? '*'
                    : [queryKey[1].week]
                : null,
            year: queryKey[1].year
                ? queryKey[1].year === '*'
                    ? '*'
                    : [queryKey[1].year]
                : null,
        },
    });

    return {
        ...resp,
        data: resp.data?.map(
            shapeRow({
                type: pluralDimensionToSingle[queryKey[1].paginateBy],
                entities: indexEntities(resp.entities),
                paginateBy: queryKey[1].paginateBy,
            }),
        ),
    };
};

const useTikTokData = createSourceDataHook({
    queryName: 'TikTokData',
    queryFn,
    seedEntitiesFor: DATA_SOURCES.TIKTOK,
});

export default useTikTokData;
